<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import SalesDetailPartyDocuments from './SalesDetailPartyDocuments.vue';

const props = defineProps({
  parties: { type: Array, default: () => [] },
  role: { type: String, required: true },
  transactionId: { type: [Number, String], required: true },
});
const emit = defineEmits(['details']);

const { t } = useI18n();
const selectedPartyId = ref('');
const principalParties = computed(() =>
  props.parties.filter(party => party.party_role === props.role)
);
const selectableParties = computed(() =>
  principalParties.value.flatMap(party => [
    party,
    ...(party.representatives || []),
  ])
);
const selectedParty = computed(() =>
  selectableParties.value.find(
    party => String(party.id) === String(selectedPartyId.value)
  )
);
const title = computed(() =>
  props.role === 'vendedor'
    ? t('POST_SALE.TOPICS.DOCUMENTS.SELLERS')
    : t('POST_SALE.TOPICS.DOCUMENTS.BUYERS')
);
const emptyLabel = computed(() =>
  props.role === 'vendedor'
    ? t('POST_SALE.TOPICS.DOCUMENTS.EMPTY_SELLERS')
    : t('POST_SALE.TOPICS.DOCUMENTS.EMPTY_BUYERS')
);
const detailsLabel = computed(() =>
  props.role === 'vendedor'
    ? t('POST_SALE.TOPICS.ACTIONS.VIEW_SELLER_DETAILS')
    : t('POST_SALE.TOPICS.ACTIONS.VIEW_BUYER_DETAILS')
);

watch(
  selectableParties,
  parties => {
    if (
      !parties.some(party => String(party.id) === String(selectedPartyId.value))
    ) {
      selectedPartyId.value = parties[0]?.id || '';
    }
  },
  { immediate: true }
);
</script>

<template>
  <LaisCard
    :title="title"
    :prefix-icon="
      role === 'vendedor' ? 'i-lucide-user-round-check' : 'i-lucide-user-round'
    "
  >
    <template #titleAction>
      <div class="flex items-center gap-2">
        <span
          v-if="principalParties.length > 1"
          class="rounded-full bg-lais-bg-soft px-2.5 py-1 text-xs font-semibold text-lais-text-body"
        >
          {{ principalParties.length }}
        </span>
        <select
          v-if="selectableParties.length"
          v-model="selectedPartyId"
          :aria-label="t('POST_SALE.TOPICS.DOCUMENTS.SELECT_PERSON')"
          :disabled="selectableParties.length === 1"
          class="h-9 max-w-48 rounded-lais-sm border border-lais-border bg-white px-3 text-sm text-lais-text focus:border-lais-primary focus:outline-none disabled:bg-lais-bg-soft"
        >
          <option
            v-for="party in selectableParties"
            :key="party.id"
            :value="party.id"
          >
            {{ party.display_name }}
          </option>
        </select>
      </div>
    </template>

    <SalesDetailPartyDocuments
      v-if="selectedParty"
      :documents="selectedParty.documents || []"
      :party-id="selectedParty.id"
      :transaction-id="transactionId"
    />
    <p v-else class="text-sm text-lais-text-muted">
      {{ emptyLabel }}
    </p>
    <template #footer>
      <LaisPrimaryButton variant="ghost" @click="emit('details', role)">
        {{ detailsLabel }}
      </LaisPrimaryButton>
    </template>
  </LaisCard>
</template>
